import { ReactNode, useEffect, useRef, useState } from "react";
import { List, AutoSizer, ListRowRenderer } from "react-virtualized";

type VirtualListProps<T> = {
  items: T[];
  itemHeight: number;
  overscan?: number;
  renderItem: (item: T, index: number) => ReactNode;
  className?: string;
  style?: React.CSSProperties;
  "data-testid"?: string;
};

export default function VirtualList<T>({
  items,
  itemHeight,
  overscan = 5,
  renderItem,
  className,
  style,
  ...rest
}: VirtualListProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerReady, setContainerReady] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || containerReady) return;

    if (el.clientHeight > 0) {
      setContainerReady(true);
      return;
    }

    const observer = new ResizeObserver((entries) => {
      if (entries[0]?.contentRect.height > 0) {
        setContainerReady(true);
        observer.disconnect();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [containerReady]);

  const rowRenderer: ListRowRenderer = ({ index, key, style: rowStyle }) => (
    <div
      key={key}
      style={{ ...rowStyle, overflow: "hidden", boxSizing: "border-box" }}
    >
      {renderItem(items[index], index)}
    </div>
  );

  // Test environment fallback (jsdom has no layout)
  if (!containerReady) {
    return (
      <div
        ref={containerRef}
        className={className}
        data-testid={rest["data-testid"]}
        style={{ height: "100%", overflow: "auto", ...style }}
      >
        {items.map((item, index) => (
          <div key={index}>{renderItem(item, index)}</div>
        ))}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={className}
      data-testid={rest["data-testid"]}
      style={{
        height: "100%",
        overflow: "hidden",
        minHeight: 0,
        ...style
      }}
    >
      {items.length > 0 && (
        <AutoSizer>
          {({ width, height }) =>
            width > 0 && height > 0 ? (
              <List
                width={width}
                height={height}
                rowCount={items.length}
                rowHeight={itemHeight}
                rowRenderer={rowRenderer}
                overscanRowCount={overscan}
                style={{ outline: "none" }}
              />
            ) : null
          }
        </AutoSizer>
      )}
    </div>
  );
}
